import type { QueuedMutation } from '../queue/types';
import { isStaleWriteError, staleWriteMessage } from '../react/staleWriteFailure';

/**
 * Finds queued mutations the server refused as a stale write (see staleWriteFailure.ts)
 * so the person is told, whenever the refusal happened.
 *
 * A save made while online learns of a refusal from its own call. A save queued offline is
 * replayed in the background, possibly after the user has moved on; its refusal only lands in
 * the queue as a failed row. Each refusal is reported once per queued mutation.
 */
export interface StaleWriteFailure {
  readonly mutationId: string;
  /** The server's wording, without the stale-write marker. */
  readonly message: string;
  readonly payload: unknown;
  /** True for a stale-write refusal; false for any other permanent failure (only when staleOnly is false). */
  readonly stale: boolean;
}

interface QueueReader {
  getByStatus(status: 'failed'): Promise<readonly QueuedMutation[]>;
}

interface StatusSource {
  onStatusChange(callback: () => void): () => void;
}

export interface WatchStaleWriteFailuresOptions {
  readonly queue: QueueReader;
  readonly engine: StatusSource;
  /** Only mutations of this queue type are considered. */
  readonly mutationType: string;
  /** Only mutations whose payload[scopeField] === scopeValue (e.g. one survey). */
  readonly scopeField: string;
  readonly scopeValue: string;
  /**
   * Default true: report only stale-write refusals. Pass false to report EVERY permanently failed
   * mutation of this type and scope (e.g. a save refused because a placement option still uses what
   * it removes), with `stale` telling them apart.
   */
  readonly staleOnly?: boolean;
  readonly onFailure: (failure: StaleWriteFailure) => void;
  /** Called when the queue cannot be read, so the failure is not silent. */
  readonly onError: (error: unknown) => void;
}

/** Start watching; returns the stop function. Checks once at start, then on every status change. */
export function watchStaleWriteFailures(options: WatchStaleWriteFailuresOptions): () => void {
  const { queue, engine, mutationType, scopeField, scopeValue, onFailure, onError } = options;
  const staleOnly = options.staleOnly ?? true;
  const reported = new Set<string>();
  let stopped = false;

  const check = async (): Promise<void> => {
    try {
      const failed = await queue.getByStatus('failed');
      if (stopped) return;
      for (const m of failed) {
        const stale = isStaleWriteError(m.error);
        if (m.type !== mutationType || reported.has(m.id) || (staleOnly && !stale)) continue;
        const scope = (m.payload as Record<string, unknown> | null)?.[scopeField];
        if (scope !== scopeValue) continue;
        reported.add(m.id);
        onFailure({
          mutationId: m.id,
          message: staleWriteMessage(m.error ?? 'The save failed.'),
          payload: m.payload,
          stale,
        });
      }
    } catch (err) {
      if (!stopped) onError(err);
    }
  };

  // onStatusChange calls back immediately, which is the "check once at start".
  const unsubscribe = engine.onStatusChange(() => { void check(); });
  return () => {
    stopped = true;
    unsubscribe();
  };
}
